import { usePerms } from 'contexts/PermsContext';
import { selectSelectedDeviceId } from 'features/Integrations/state/selectors/deviceSelectors';
import { openStageSettings } from 'features/Integrations/state/slices/deviceSlice';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  useChangeFloodStageDeviceSettingsMutation,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import CopySettingsBar from './components/CopySettingsBar';
import FloodDeviceSettingsForm from './features/FloodDeviceSettingsForm/FloodDeviceSettingsForm';
import { DEFAULT_SIM, makeDefaultSim } from './features/FloodSimSettings/config/floodSimConfig';
import FloodSimSettings from './features/FloodSimSettings/FloodSimSettings';
import { addUpstream, buildLink, deviceDistanceMiles, deviceLabel, routingOf, travelMinutes, upstreamLinksOf } from './features/FloodSimSettings/utils/floodSimRouting.utils';

export default function FloodDeviceSettings({ mobile = false }) {
  const perms = usePerms();
  const dispatch = useDispatch();
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [changeFloodStageDeviceSettings] = useChangeFloodStageDeviceSettingsMutation();
  const selectedDeviceId = useSelector(selectSelectedDeviceId);

  const device = connectedDevices.find((device) => device.id === selectedDeviceId) || null;
  const [commandSent, setCommandSent] = useState(false);

  // The form's baseline, from the device as the list currently knows it.
  const settingsFrom = (d) => ({
    node_id: d?.node_id,
    uuid: d?.uuid,
    imei: d?.IMEI,
    depthDet1: d?.device_settings?.depthDet1,
    depthDet2: d?.device_settings?.depthDet2,
    depthDet3: d?.device_settings?.depthDet3,
    depthHoldHours: d?.device_settings?.depthHoldHours,
    ffTheshold: d?.device_settings?.ffTheshold || 0, // Use fallback value if null
    dropThresh: d?.device_settings?.dropThresh,
    mode: d?.device_settings?.mode,
    samp: d?.device_settings?.samp,
    dDetImgEnable: d?.device_settings?.dDetImgEnable,
    ffiImgEnable: d?.device_settings?.ffiImgEnable ?? 0, // Use fallback value if null
    dRateImgEnable: d?.device_settings?.dRateImgEnable,
    hResImgEnable: d?.device_settings?.hResImgEnable,
    water_level_adj: d?.device_settings?.water_level_adj,
    externalDevice: d?.device_settings?.externalDevice,
    volConstant: d?.device_settings?.volConstant,
    calibration: d?.device_settings?.calibration,
    calType: d?.device_settings?.calType,
    resetCalibration: false,
    imgHoldEnable: d?.device_settings?.imgHoldEnable,
    imgHoldStart: d?.device_settings?.imgHoldStart,
    imgHoldEnd: d?.device_settings?.imgHoldEnd,
    imageRequest: false,
    gpsSync: false,
    ipAddress: d?.device_settings?.ipAddress,
    protocol: d?.device_settings?.protocol,
    port: d?.device_settings?.port,
    reboot: false,
    sim: d?.device_settings?.sim ?? makeDefaultSim(),
  });
  const [deviceSettings, setDeviceSettings] = useState(() => settingsFrom(device));
  // Downstream links staged on the Waterway card — written by Save, which then also
  // updates each of those devices. ONE Save (Mike, 2026-08-28: "there are alot of save").
  const [pendingDownstream, setPendingDownstream] = useState([]);
  useEffect(() => {
    setPendingDownstream([]);
  }, [device?.id]);

  // Reseed when the device itself changes, and when the list refreshes with
  // nothing unsaved in the form. The save invalidates the device list, so
  // after it the list carries the settings that were just written and the form
  // follows — before this, the form was seeded once on mount and a reopened
  // modal showed the previous settings (Mike, 2026-08-28: "it seems to reset").
  // A form with unsaved edits is left alone: a refresh must not wipe typing.
  useEffect(() => {
    setDeviceSettings((prev) => {
      const fresh = settingsFrom(device);
      const unsaved = Object.keys(getChangedFields(device?.device_settings, prev)).length > 0;
      return unsaved && prev.node_id === fresh.node_id ? prev : fresh;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.id, device?.device_settings]);

  const handleInputChange = (field, value) => {
    setDeviceSettings((prevSettings) => ({
      ...prevSettings,
      [field]: value,
    }));
  };

  const getChangedFields = (original = {}, updated = {}) => {
    let changes = {};

    for (let key in updated) {
      // Identifiers and non-editable flags — never sent
      if (key === 'node_id' || key === 'uuid' || key === 'imei' || key === 'resetCalibration') {
        continue;
      }

      // Action-request flags default to false/0; only send when the user has activated them
      if (key === 'reboot' || key === 'imageRequest' || key === 'gpsSync') {
        if (updated[key]) changes[key] = updated[key];
        continue;
      }

      if (key === 'sim') {
        if (JSON.stringify(original[key]) !== JSON.stringify(updated[key])) {
          changes[key] = updated[key];
        }
        continue;
      }

      if (original[key] === null && updated[key] === 0) {
        // Treat null in original and 0 in updated as unchanged for specific fields
        if (key === 'ffTheshold' || key === 'ffiImgEnable') {
          continue;
        }
      }

      if (updated[key] !== original[key]) {
        changes[key] = updated[key];
      }
    }

    // cursor_state is the simulator's own bookkeeping, not a setting. Strip it
    // WITHOUT mutating: `changes.sim` can be the RTK Query cache object, which
    // is frozen in development, and `delete` on a frozen object throws. That
    // was the crash on jumping to another device — for one render the new
    // device's settings met the previous device's form (Mike, 2026-08-28).
    if (changes.sim && typeof changes.sim === 'object' && 'cursor_state' in changes.sim) {
      const { cursor_state: _cursor, ...sim } = changes.sim;
      changes.sim = sim;
    }
    return changes;
  };

  const nameOf = (id) => deviceLabel(connectedDevices.find((d) => d.id === id) ?? { id });

  /** The settings PATCH answers 200 with `applied` / `rejected`; a rejected key is a silent failure unless it is shown. */
  const rejectedOf = (result) => {
    const body = result?.data ?? result;
    return Array.isArray(body?.rejected) ? body.rejected : [];
  };

  /**
   * The ONE save. In order: this device's changed settings (thresholds, the
   * sim block with its upstream links), then every staged downstream link —
   * each of those devices gets this one added to its upstreams and takes this
   * scenario. Every refusal is named; a rejected key on a 200 is a warning,
   * not a success (a backend without the waterway update refuses
   * `routing.upstreams` exactly that way).
   */
  const sendCommand = async () => {
    const changedFields = getChangedFields(device?.device_settings, deviceSettings);
    const done = [];
    const warnings = [];
    try {
      if (Object.keys(changedFields).length > 0) {
        const result = await changeFloodStageDeviceSettings({
          deviceId: device.id,
          deviceSettings: changedFields,
        }).unwrap();
        const rejected = rejectedOf(result);
        if (rejected.length) warnings.push(`${deviceLabel(device)}: the server refused ${rejected.join('; ')}`);
        done.push(deviceLabel(device));
      }
    } catch (error) {
      console.error(error?.message ?? error);
      toast.error(`${deviceLabel(device)}: ${error?.data?.message ?? error?.message ?? 'the server refused the settings'}. Nothing else was written.`);
      return false;
    }

    // The staged downstream links: this device, as just saved, becomes an upstream of each.
    const self = { ...device, device_settings: { ...device.device_settings, sim: deviceSettings.sim } };
    const lookup = (id) => (id === device.id ? self : connectedDevices.find((d) => d.id === id));
    const mine = deviceSettings.sim ?? DEFAULT_SIM;
    const stillPending = [];
    for (const p of pendingDownstream) {
      const target = connectedDevices.find((d) => d.id === p.id);
      if (!target) continue;
      const built = buildLink({ upstream: self, downstream: target, speedMph: p.speedMph });
      const routing = addUpstream(routingOf(target), built.link, target, lookup);
      const { cursor_state: _cursor, ...targetSim } = target.device_settings?.sim ?? DEFAULT_SIM;
      const sim = {
        ...targetSim,
        sim_type: 'scenario',
        scenario_name: mine.scenario_name,
        scenario_params: mine.scenario_params ?? {},
        routing,
      };
      try {
        const result = await changeFloodStageDeviceSettings({ deviceId: target.id, deviceSettings: { sim } }).unwrap();
        const rejected = rejectedOf(result);
        if (rejected.length) warnings.push(`${deviceLabel(target)}: the server refused ${rejected.join('; ')}`);
        done.push(deviceLabel(target));
      } catch (error) {
        console.error(error?.message ?? error);
        warnings.push(`${deviceLabel(target)}: ${error?.data?.message ?? error?.message ?? 'the server refused the settings'} — the link stays staged`);
        stillPending.push(p);
      }
    }
    setPendingDownstream(stillPending);

    if (done.length) toast.success(`Saved: ${done.join(', ')}`);
    for (const w of warnings) toast.warn(w, { autoClose: 12000 });
    if (done.length && warnings.length === 0) {
      setCommandSent(true);
      setTimeout(() => setCommandSent(false), 5000);
    }
    return warnings.length === 0;
  };

  // ── Waterway actions ──

  /** Open another Stage device in this modal. Never saves: the card disables Open while anything is unsaved. */
  const openDevice = (deviceId) => {
    if (hasChanges) return;
    dispatch(openStageSettings(deviceId));
  };

  const stageDownstream = (downstreamId, speedMph) =>
    setPendingDownstream((prev) => [...prev.filter((p) => p.id !== downstreamId), { id: downstreamId, speedMph }]);
  const unstageDownstream = (downstreamId) =>
    setPendingDownstream((prev) => prev.filter((p) => p.id !== downstreamId));

  const canEditFloodStage = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['Create-EditFloodStageConnections'],
  );

  // The simulator settings are behind their own lock (Mike, 2026-08-28): a
  // maintenance operator (or super admin) AND ManageFloodSimulation on the
  // role — no admin bypass on the key. The backend refuses the same way.
  const canEditSim =
    perms.testPermission([8, 12, 13], ['ManageFloodSimulation']) && perms.isMaintenanceOperator();
  const hasChanges = useMemo(() => {
    const changes = getChangedFields(device?.device_settings, deviceSettings);
    return Object.keys(changes).length > 0 || pendingDownstream.length > 0;
  }, [device?.device_settings, deviceSettings, pendingDownstream]);

  /**
   * What Save will do, one line per device it touches — so "which is which"
   * is never a guess (Mike, 2026-08-28).
   */
  const savePlan = useMemo(() => {
    if (!device) return [];
    const lines = [];
    const changes = getChangedFields(device?.device_settings, deviceSettings);
    const fieldKeys = Object.keys(changes).filter((k) => k !== 'sim');
    const parts = [];
    if (fieldKeys.length) parts.push(fieldKeys.join(', '));
    if (changes.sim) {
      const before = upstreamLinksOf(device);
      const after = upstreamLinksOf({ ...device, device_settings: { sim: deviceSettings.sim } });
      const added = after.filter((l) => !before.some((b) => b.uid === l.uid));
      const removed = before.filter((b) => !after.some((l) => l.uid === b.uid));
      const changedSpeed = after.filter((l) => before.some((b) => b.uid === l.uid && Math.round(b.travelMinutes) !== Math.round(l.travelMinutes)));
      parts.push('demo settings');
      added.forEach((l) => parts.push(`${nameOf(l.uid)} now feeds into ${deviceLabel(device)} (about ${Math.round(l.travelMinutes)} min)`));
      removed.forEach((l) => parts.push(`${nameOf(l.uid)} no longer feeds into ${deviceLabel(device)}`));
      changedSpeed.forEach((l) => parts.push(`the link from ${nameOf(l.uid)} is now about ${Math.round(l.travelMinutes)} min`));
    }
    if (parts.length) lines.push({ device: deviceLabel(device), what: parts.join('; ') });
    for (const p of pendingDownstream) {
      const target = connectedDevices.find((d) => d.id === p.id);
      const min = travelMinutes(deviceDistanceMiles(device, target), p.speedMph);
      lines.push({
        device: nameOf(p.id),
        what: `${deviceLabel(device)} now feeds into it (about ${Math.round(min ?? 0)} min at ${p.speedMph} mph); it takes this scenario`,
      });
    }
    return lines;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, device?.device_settings, deviceSettings, pendingDownstream, connectedDevices]);

  if (mobile) {
    return (
      <Alert variant="warning" className="m-3">
        This feature is currently not implemented. Please manage Flood Stage devices from the
        desktop website.
      </Alert>
    );
  }

  return (
    <Container fluid className="py-3 text-white">
      <div className="d-flex flex-column gap-3 min-h-75vh">
        {/* Copy settings from another device */}
        <CopySettingsBar
          connectedDevices={connectedDevices}
          setDeviceSettings={setDeviceSettings}
          enabled={canEditFloodStage}
        />
        {device && (
          // Main device settings form, holds all the general Settings
          <FloodDeviceSettingsForm
            deviceSettings={deviceSettings}
            setDeviceSettings={setDeviceSettings}
            handleInputChange={handleInputChange}
            device={device}
            enabled={canEditFloodStage}
          />
        )}
        {device && canEditSim && (
          // Advanced settings for Flood SIM configuration, only shown if user has SIM edit permissions
          <FloodSimSettings
            sim={deviceSettings.sim}
            device={device}
            onChange={(newSim) => setDeviceSettings((prev) => ({ ...prev, sim: newSim }))}
            enabled={canEditSim}
            hasUnsavedChanges={hasChanges}
            onOpenDevice={openDevice}
            pendingDownstream={pendingDownstream}
            onStageDownstream={stageDownstream}
            onUnstageDownstream={unstageDownstream}
          />
        )}
        {canEditFloodStage && (
          <div className="small border border-secondary rounded px-3 py-2">
            <div className="fw-semibold">{savePlan.length ? 'Save will:' : 'Nothing to save — no changes yet.'}</div>
            {savePlan.map((line) => (
              <div key={line.device}>
                <span className="text-warning fw-semibold">{line.device}</span>
                <span className="text-secondary"> — {line.what}</span>
              </div>
            ))}
          </div>
        )}
        {canEditFloodStage && (
          <div className="d-flex justify-content-end align-items-center gap-3">
            {commandSent && (
              <span className="text-success fw-semibold">Command sent to server!</span>
            )}
            <Button
              size="lg"
              onClick={sendCommand}
              disabled={!hasChanges}
              variant={hasChanges ? 'primary' : 'secondary'}
              className="aware-save-button"
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </Container>
  );
}
